import { useCallback, useEffect, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "barista:theme";

function systemDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function apply(pref: ThemePreference): void {
  const dark = pref === "dark" || (pref === "system" && systemDark());
  const el = document.documentElement;
  el.classList.add("theme-switching");
  el.classList.toggle("dark", dark);
  window.setTimeout(() => el.classList.remove("theme-switching"), 200);
}

/**
 * The theme preference: light / dark / system (default). The pre-paint
 * script in index.html stamps the first frame; this hook owns changes and
 * follows OS flips while in "system".
 */
export function useTheme(): {
  preference: ThemePreference;
  resolved: "light" | "dark";
  setPreference: (pref: ThemePreference) => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  });
  const [osDark, setOsDark] = useState(systemDark);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setOsDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Re-applies on OS flips too: `apply` reads matchMedia itself, so osDark
  // is the trigger, not an input.
  // biome-ignore lint/correctness/useExhaustiveDependencies: osDark IS the re-run signal
  useEffect(() => {
    apply(preference);
  }, [preference, osDark]);

  const setPreference = useCallback((pref: ThemePreference) => {
    if (pref === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, pref);
    setPreferenceState(pref);
  }, []);

  const resolved =
    preference === "dark" || (preference === "system" && osDark)
      ? "dark"
      : "light";

  return { preference, resolved, setPreference };
}
